import React, { useMemo, useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Input,
  InputNumber,
  Switch,
  Upload,
  Button,
  Typography,
  message,
  Steps,
  Select,
  Table,
  // Checkbox,
  Divider,
  Space,
  Checkbox,
  // Tooltip,
  // Alert,
  // Tag,
} from "antd";

import {
  ArrowLeftOutlined,
  DeleteOutlined,
  CheckCircleTwoTone,
  PaperClipOutlined,
  // ExclamationCircleOutlined,
  CopyOutlined,
  ReloadOutlined,
} from "@ant-design/icons";

import { theme } from "antd";
import docImg from "../../../assets/icons/file-icon.svg";
import { apiBaseUrl } from "../../../commonUtils/commonUtils";

import "./mobCredit.css";
import InlineUpload from "../../../components/Input/InlineUpload/InlineUpload";
// import NoteBanner from "../../../components/NoteBanner";
import BusinessPhoneField from "./BusinessPhoneField";
//import onSubmitAll_createMobCredit from "../../../services/saveMobCredit";
import {
  validateGSTIN,
  validatePAN,
  validateAadhaar,
  validateSecondaryPhone,
  // normalizePAN,
  // normalizeGSTIN,
} from "../../../utils/validators";

const { Title, Text } = Typography;

// const { Dragger } = Upload;

export default function CreateMobCreditForm() {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(0);
  const [saving, setSaving] = useState(false);

  // Controlled Upload lists (kept as arrays, no TS types)
  const [gstDoc, setGstDoc] = useState([]);
  const [panDoc, setPanDoc] = useState([]);
  const [aadhaarDoc, setAadhaarDoc] = useState([]);
  const [msmeDoc, setMsmeDoc] = useState([]);
  // eslint-disable-next-line
  const [cheques, setCheques] = useState([]); // step 3 bulk (existing)
  const [pdcFile, setPdcFile] = useState([]); // NEW: single cheque in step 2
  const [creditFlags, setCreditFlags] = useState(null);
  const [canContinue, setCanContinue] = useState(false);
  console.log("🚀 ~ CreateMobCreditForm ~ canContinue:", canContinue)
  const [accountAllowed, setAccountAllowed] = useState(null);
  const [approvalDetails, setApprovalDetails] = useState(null);

  const initialValues = {
    business_name: "",
    business_phone: "",
    business_gst: "",
    pan_card: "",
    aadhaar_card: "",
    email: "",
    secondary_phone: "",
    // Step 2 defaults
    status: "active",
    amount_sanctioned: 0,
    line_of_credit_duration: 21,
    terms: getTermsText(21),
    pdc_not_received: true,
    continue_without_cheque: false,
    // Step 3 defaults
    is_activated: false,
    risk_gstr3b: "on_time",
    risk_gstr1: "minor_delays",
  };

  function getTermsText(days) {
    return `This is 'Trade Credit' for being a VIP MOB client.\nThis is extended for up to ${days} days period from the delivery date of the order.\nTimely repayment to MOB would insure credit limit increase. Reach out to your account manager with MOB for any queries.`;
  }

  const cleanMessage = approvalDetails?.message
    ?.replace(/f"([^"]+)"/g, "$1") // remove f"..."
    ?.trim();

  const riskOptions = [
    { value: "on_time", label: "On time", score: 2 },
    { value: "minor_delays", label: "Minor delays", score: 2 },
    { value: "frequent_delays", label: "Frequent delays", score: 1 },
    { value: "non_filing", label: "Non-filing", score: 0 },
  ];

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];
  const MAX_FILE_MB = 5;

  const isCreditBlocked = useMemo(() => {
    if (!creditFlags) return false;

    // Some backends return: { status: true/false, data: { lineOfCreditEnabled: boolean, ... } }
    // Others may flatten:  { lineOfCreditEnabled: boolean }
    const status =
      typeof creditFlags.status === "boolean" ? creditFlags.status : true;

    const lineOfCreditEnabled =
      creditFlags?.data?.lineOfCreditEnabled ??
      creditFlags?.lineOfCreditEnabled ??
      false;

    // Disable only when status is false OR lineOfCreditEnabled is true
    return status === false || lineOfCreditEnabled === true;
  }, [creditFlags]);

  async function checkAccount(phone) {
    if (!phone || String(phone).trim().length < 10) {
      setAccountAllowed(null);
      return;
    }
    try {
      const r = await fetch(
        `${apiBaseUrl}/accounts/account-checker/?phone_number=${encodeURIComponent(
          phone
        )}`
      );
      const d = await r.json();
      setAccountAllowed(d?.status !== false);
    } catch {
      setAccountAllowed(true);
    }
  }

  const handleValuesChange = (changed, all) => {
    if ("business_phone" in changed) {
      checkAccount(all.business_phone);
    }
    if ("pdc_received" in changed && !all.pdc_received) {
      setPdcFile([]);
    }
    if ("continue_without_cheque" in changed && !all.continue_without_cheque) {
      form.setFieldsValue({ reason: undefined });
    }
    // recomputeCanContinue();
  };

  async function recomputeCanContinue() {
    const ok = await validateStep();
    if (!ok) {
      setCanContinue(false);
      return;
    }
    if (current === 1) {
      const cont = !!form.getFieldValue("continue_without_cheque");
      const reason = (form.getFieldValue("reason") || "").trim();
      const hasPdc = Array.isArray(pdcFile) && pdcFile.length > 0;
      if (!cont && !hasPdc) {
        setCanContinue(false);
        return;
      }
      if (cont && reason.length < 10) {
        setCanContinue(false);
        return;
      }
    }
    if (accountAllowed === false) {
      setCanContinue(false);
      return;
    }
    setCanContinue(true);
  }

  function isValidFile(file) {
    const okType = ALLOWED_TYPES.includes(file.type);
    const okSize = file.size / 1024 / 1024 <= MAX_FILE_MB;
    return okType && okSize;
  }

  function filterValidFiles(fileList) {
    const valid = [];
    fileList.forEach((f) => {
      const file = f.originFileObj || f;
      if (file && isValidFile(file)) valid.push(f);
    });
    return valid;
  }

  const riskRows = useMemo(
    () => [
      { key: "gstr3b", param: "GSTR-3B filing", field: "risk_gstr3b" },
      { key: "gstr1", param: "GSTR-1 filing", field: "risk_gstr1" },
    ],
    []
  );

  const riskColumns = [
    {
      title: "Parameters",
      dataIndex: "param",
      key: "param",
      render: (t) => (
        <div className="risk-param">
          <span className="icon-image">
            <img src={docImg} alt="doc" className="doc-icon" />
          </span>
          <Text>{t}</Text>
        </div>
      ),
    },
    {
      title: "Filing details",
      key: "filing",
      render: (_, row) => (
        <Form.Item name={row.field} style={{ margin: 0 }}>
          <Select
            options={riskOptions}
            popupMatchSelectWidth
            style={{ width: 220 }}
          />
        </Form.Item>
      ),
    },
    {
      title: "Score",
      key: "score",
      align: "right",
      render: (_, row) => {
        const val = form.getFieldValue(row.field) ?? initialValues[row.field];
        const score = riskOptions.find((o) => o.value === val)?.score ?? 0;
        return <Text strong>{score}</Text>;
      },
    },
  ];
  // eslint-disable-next-line
  const norm = (e) => (Array.isArray(e) ? e : e?.fileList ?? []);

  const validateStep = async () => {
    const stepFieldsByIndex = [
      [
        "business_name",
        "business_phone",
        "business_gst",
        "pan_card",
        "aadhaar_card",
        "email",
        "secondary_phone",
        "risk_gstr3b",
        "risk_gstr1",
      ],
      ["status", "amount_sanctioned", "line_of_credit_duration", "terms"],
      ["is_activated"],
    ];
    const stepFields = stepFieldsByIndex[current] || [];
    try {
      await form.validateFields(stepFields);
      return true;
    } catch (e) {
      return false;
    }
  };

  const autosave = async () => {
    try {
      setSaving(true);
      await new Promise((r) => setTimeout(r, 500));
      message.success({
        content: "Draft saved",
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
      });
    } catch {
      message.error("Autosave failed");
    } finally {
      setSaving(false);
    }
  };

  const handleContinue = async () => {
    const ok = await validateStep();
    if (!ok) return;

    if (current === 1) {
      const cont = !!form.getFieldValue("continue_without_cheque");
      const reason = (form.getFieldValue("reason") || "").trim();
      const hasPdc = Array.isArray(pdcFile) && pdcFile.length > 0;

      if (!cont && !hasPdc) {
        message.error(
          "Please upload at least one cheque OR choose 'Continue without uploading cheque' with a valid reason."
        );
        return;
      }
      if (cont && reason.length < 10) {
        message.error(
          "Please provide a reason with at least 10 characters to continue without cheque."
        );
        return;
      }
    }

    try {
      await autosave();

      if (current === 1) {
        await submitAtStep2(); // <-- call backend here at end of Step 2
        setCurrent(2); // move to Step 3 only after backend returns
        message.success("Details saved. Review & send approval.");
        return;
      }

      setCurrent((c) => Math.min(c + 1, 2));
    } catch (e) {
      message.error(e?.message || "Save failed. Please try again.");
    }
  };

  const handleBack = () => {
    if (current === 0) return window.history.back();
    setCurrent((c) => Math.max(c - 1, 0));
  };

  function buildFormDataForSubmit(v) {
    const fd = new FormData();

    const scoreMap = {};
    riskOptions.forEach((o) => {
      scoreMap[o.value] = o.score;
    });
    const s1 =
      scoreMap[v.risk_gstr3b] ?? scoreMap[initialValues.risk_gstr3b] ?? 0;
    const s2 =
      scoreMap[v.risk_gstr1] ?? scoreMap[initialValues.risk_gstr1] ?? 0;
    const score_field = {
      gstr3b: v.risk_gstr3b ?? initialValues.risk_gstr3b,
      gstr1: v.risk_gstr1 ?? initialValues.risk_gstr1,
      total_points: s1 + s2,
    };

    appendIf(fd, "phone_number", v.business_phone);
    appendIf(fd, "business_name", v.business_name);
    appendIf(fd, "business_phone", v.business_phone);
    appendIf(fd, "business_gst", (v.business_gst || "").toUpperCase());
    appendIf(fd, "email", v.email);
    appendIf(fd, "secondary_phone", v.secondary_phone);
    appendIf(fd, "amount_sanctioned", String(v.amount_sanctioned ?? 0));
    appendIf(
      fd,
      "line_of_credit_duration",
      String(v.line_of_credit_duration ?? 21)
    );

    const safeReason =
      v.reason || (v.pdc_received ? "PDC received" : "PDC not received yet");
    appendIf(fd, "reason", safeReason);

    fd.append("score_field", JSON.stringify(score_field));

    appendFirstFile(fd, gstDoc, "business_gst_doc");
    appendFirstFile(fd, msmeDoc, "msme_certificate");

    if (Array.isArray(panDoc) && panDoc.length) {
      panDoc.forEach((f, i) => {
        if (!f?.originFileObj) return;
        fd.append(`pan_docs.${i}.pan_card`, (v.pan_card || "").toUpperCase());
        fd.append(
          `pan_docs.${i}.pan_docs`,
          f.originFileObj,
          f.name || `pan_${i + 1}`
        );
      });
    }

    if (Array.isArray(aadhaarDoc) && aadhaarDoc.length) {
      aadhaarDoc.forEach((f, i) => {
        if (!f?.originFileObj) return;
        fd.append(`aadhaar_docs.${i}.aadhaar_card`, v.aadhaar_card || "");
        fd.append(
          `aadhaar_docs.${i}.aadhaar_docs`,
          f.originFileObj,
          f.name || `aadhaar_${i + 1}`
        );
      });
    }

    if (Array.isArray(pdcFile) && pdcFile.length) {
      pdcFile.forEach((f, i) => {
        if (!f?.originFileObj) return;
        fd.append(
          `cheques.${i}.cheque`,
          f.originFileObj,
          f.name || `cheque_${i + 1}`
        );
        fd.append(`cheques.${i}.status`, f.status || "Active");
      });
    }

    return fd;
  }

  async function submitAtStep2() {
    await form.validateFields(); // validate all current fields too
    const v = form.getFieldsValue(true);
    const fd = buildFormDataForSubmit(v);

    const resp = await fetch(`${apiBaseUrl}/accounts/mob-line-of-credit/`, {
      method: "POST",
      body: fd,
    });

    const data = await resp.json().catch(() => ({}));
    if (!resp.ok || data?.status === false) {
      const detailsErr =
        (data && data.errors && flattenDjangoErrors(data.errors)) ||
        data?.detail ||
        data?.message ||
        "HTTP " + resp.status;
      throw new Error(detailsErr);
    }

    const backendDetails = data?.data?.details || data?.details || null;
    setApprovalDetails(backendDetails || null);
  }

  const onSubmitAll = async () => {
    try {
      await form.validateFields();
      const v = form.getFieldsValue(true);

      // build score_field object (required by backend)
      const scoreMap = {};
      riskOptions.forEach((o) => {
        scoreMap[o.value] = o.score;
      });
      const s1 =
        scoreMap[v.risk_gstr3b] ?? scoreMap[initialValues.risk_gstr3b] ?? 0;
      const s2 =
        scoreMap[v.risk_gstr1] ?? scoreMap[initialValues.risk_gstr1] ?? 0;
      const score_field = {
        gstr3b: v.risk_gstr3b ?? initialValues.risk_gstr3b,
        gstr1: v.risk_gstr1 ?? initialValues.risk_gstr1,
        total_points: s1 + s2,
      };

      const fd = new FormData();

      // required write-only
      appendIf(fd, "phone_number", v.business_phone);

      // core fields
      appendIf(fd, "business_name", v.business_name);
      appendIf(fd, "business_phone", v.business_phone);
      appendIf(fd, "business_gst", (v.business_gst || "").toUpperCase());
      appendIf(fd, "email", v.email);
      appendIf(fd, "secondary_phone", v.secondary_phone);

      // credit meta
      appendIf(fd, "amount_sanctioned", String(v.amount_sanctioned ?? 0));
      appendIf(
        fd,
        "line_of_credit_duration",
        String(v.line_of_credit_duration ?? 21)
      );

      // reason vs cheques rule on backend
      const safeReason =
        v.reason || (v.pdc_received ? "PDC received" : "PDC not received yet");
      appendIf(fd, "reason", safeReason);
      // score_field JSON
      fd.append("score_field", JSON.stringify(score_field));

      // single-file fields
      appendFirstFile(fd, gstDoc, "business_gst_doc");
      appendFirstFile(fd, msmeDoc, "msme_certificate");

      // MULTI: PAN docs (reuse single number for now)
      if (Array.isArray(panDoc) && panDoc.length) {
        panDoc.forEach((f, i) => {
          if (!f?.originFileObj) return;
          fd.append(`pan_docs.${i}.pan_card`, (v.pan_card || "").toUpperCase());
          fd.append(
            `pan_docs.${i}.pan_docs`,
            f.originFileObj,
            f.name || `pan_${i + 1}`
          );
        });
      }

      // MULTI: Aadhaar docs (reuse single number for now)
      if (Array.isArray(aadhaarDoc) && aadhaarDoc.length) {
        aadhaarDoc.forEach((f, i) => {
          if (!f?.originFileObj) return;
          fd.append(`aadhaar_docs.${i}.aadhaar_card`, v.aadhaar_card || "");
          fd.append(
            `aadhaar_docs.${i}.aadhaar_docs`,
            f.originFileObj,
            f.name || `aadhaar_${i + 1}`
          );
        });
      }

      // Upload cheques with status from pdcFile
      if (Array.isArray(pdcFile) && pdcFile.length) {
        pdcFile.forEach((f, i) => {
          if (!f?.originFileObj) return;
          fd.append(
            `cheques.${i}.cheque`,
            f.originFileObj,
            f.name || `cheque_${i + 1}`
          );
          fd.append(`cheques.${i}.status`, f.status || "Active");
        });
      }

      for (const [k, val] of fd.entries()) {
        console.log(k, val instanceof File ? `(file) ${val.name}` : val);
      }

      // POST to your DRF endpoint
      const resp = await fetch(`${apiBaseUrl}/accounts/mob-line-of-credit/`, {
        method: "POST",
        body: fd,
        // credentials: "include", // uncomment if using session auth + csrf
        // headers: { "X-CSRFToken": getCsrfTokenFromCookie() }, // if needed
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data?.status === false) {
        const detailsErr =
          (data && data.errors && flattenDjangoErrors(data.errors)) ||
          data?.detail ||
          data?.message ||
          "HTTP " + resp.status;
        throw new Error(detailsErr);
      }
      const backendDetails = data?.data?.details || data?.details || null;
      setApprovalDetails(backendDetails || null);
      setCurrent(2);
      message.success("mobCREDIT created successfully");
    } catch (e) {
      if (e && e.errorFields) return; // antd already showing field errors
      message.error((e && e.message) || "Submission failed");
    }
  };

  // helpers (plain JS)
  function appendIf(fd, key, val) {
    if (val !== undefined && val !== null && String(val).length > 0) {
      fd.append(key, val);
    }
  }
  function appendFirstFile(fd, list, key) {
    if (!Array.isArray(list) || !list.length) return;
    const f = list[0];
    if (f && f.originFileObj) fd.append(key, f.originFileObj, f.name || "file");
  }
  function flattenDjangoErrors(errors) {
    try {
      var out = [];
      Object.keys(errors || {}).forEach(function (k) {
        var v = errors[k];
        if (Array.isArray(v)) out.push(k + ": " + v.join("; "));
        else out.push(k + ": " + String(v));
      });
      return out.join(" | ");
    } catch (e) {
      return "Validation failed";
    }
  }

  // eslint-disable-next-line
  // const ___onSubmitAll = async () => {
  //   try {
  //     await form.validateFields();
  //     const values = form.getFieldsValue(true);

  //     const fd = new FormData();
  //     Object.entries(values).forEach(([k, v]) => {
  //       if (k === "cheques") return; // handled separately
  //       if (typeof v === "boolean" || typeof v === "number")
  //         fd.append(k, String(v));
  //       else if (v != null) fd.append(k, v);
  //     });

  //     const appendFirst = (list, key) =>
  //       list?.[0]?.originFileObj &&
  //       fd.append(key, list[0].originFileObj, list[0].name);

  //     appendFirst(gstDoc, "business_gst_doc");
  //     appendFirst(panDoc, "pan_card_doc");
  //     appendFirst(aadhaarDoc, "aadhaar_card_doc");
  //     appendFirst(msmeDoc, "msme_certificate");
  //     appendFirst(pdcFile, "pdc_cheque"); // NEW: single PDC from Step 2

  //     cheques.forEach(
  //       (f, i) =>
  //         f.originFileObj &&
  //         fd.append("cheques", f.originFileObj, f.name || `cheque_${i + 1}`)
  //     );

  //     const resp = await fetch(`${apiBaseUrl}/api/mob-credit/create`, {
  //       method: "POST",
  //       body: fd,
  //     });
  //     if (!resp.ok)
  //       throw new Error(
  //         (await safeError(resp)) || `Request failed (${resp.status})`
  //       );
  //     message.success("mobCREDIT created successfully");
  //   } catch (e) {
  //     if (e?.errorFields) return;
  //     message.error(e?.message || "Submission failed");
  //   }
  // };

  const copyCustomerMsg = async () => {
    const backendText = approvalDetails?.message;
    // eslint-disable-next-line
    let text = backendText;
    if (!approvalDetails?.message) {
      message.error("No message available to copy");
      return;
    }

    await navigator.clipboard.writeText(approvalDetails.message);
    message.success("Text copied to clipboard");
  };
  useEffect(() => {
    if (current !== 0) {
      recomputeCanContinue();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, gstDoc, panDoc, aadhaarDoc, msmeDoc, pdcFile, accountAllowed]);

  /* ----------------------------- STEP 1 (polished) ----------------------------- */
  const step1 = (
    <Row gutter={[16, 16]}>
      <Row gutter={[16, 12]} style={{ width: "100%" }}>
        <Col span={24} className="header-block">
          <Title level={4} className="page-title" style={{ marginBottom: 4 }}>
            Trade credit by MOB
          </Title>
          <Text className="page-subtitle">Please add the below details</Text>
        </Col>
      </Row>

      {/* Business name + mobile */}
      <Row gutter={[16, 12]} style={{ width: "100%" }}>
        <Col xs={24} md={12}>
          <Form.Item
            label={
              <Space>
                <span>Business name</span>
                <Text className="text-danger"> *</Text>
              </Space>
            }
            name="business_name"
            rules={[{ required: true, message: "Enter business name" }]}
          >
            <Input
              placeholder="Enter business name"
              className="pan-input"
              allowClear
            />
          </Form.Item>
        </Col>

        <BusinessPhoneField form={form} onStatusChange={setCreditFlags} />
      </Row>
      {/* GSTIN row */}
      <Row
        gutter={[16, 12]}
        align="middle"
        className="pan-row"
        style={{ width: "100%" }}
      >
        <Col xs={24} md={12} className="pan-left">
          <Form.Item
            label={
              <>
                Business GSTIN<Text className="text-danger ml-2"> *</Text>
              </>
            }
            name="business_gst"
            rules={[
              { required: true, message: "Enter GSTIN" },
              {
                validator: (_, v) =>
                  validateGSTIN(v)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Enter a valid 15-character GSTIN")
                      ),
              },
            ]}
            className="upload-input"
          >
            <Input
              placeholder="Enter a valid 15-character GSTIN"
              className="pan-input"
              maxLength={15}
            />
          </Form.Item>

          {gstDoc?.[0]?.name && (
            <FilePill name={gstDoc[0].name} onDelete={() => setGstDoc([])} />
          )}
        </Col>

        <Col xs={24} md={12} className="pan-right">
          <div className="upload-side">
            <Upload
              showUploadList={false}
              beforeUpload={() => false}
              maxCount={1}
              onChange={({ fileList }) => {
                const validList = filterValidFiles(fileList);
                if (validList.length !== fileList.length) {
                  message.error(
                    `Only PDF/JPG/PNG up to ${MAX_FILE_MB}MB allowed`
                  );
                }
                setGstDoc(validList);
                form.validateFields(["business_gst"]);
              }}
            >
              <Button size="large" className="upload-btn">
                Upload
              </Button>
            </Upload>
          </div>
        </Col>
      </Row>

      {/* PAN row */}
      <Row
        gutter={[16, 12]}
        align="middle"
        className="pan-row"
        style={{ width: "100%" }}
      >
        <Col xs={24} md={12} className="pan-left">
          <Form.Item
            label={
              <>
                Pan card no<Text className="text-danger ml-2"> *</Text>
              </>
            }
            name="pan_card"
            rules={[
              { required: true, message: "Enter PAN" },
              {
                validator: (_, v) =>
                  validatePAN(v)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Enter a valid PAN (ABCDE1234F)")
                      ),
              },
            ]}
            className="upload-input"
          >
            <Input placeholder="Enter PAN" className="pan-input" />

            {panDoc?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                {panDoc.map((f, i) => (
                  <FileRow
                    key={f.uid || f.name + i}
                    name={f.name}
                    onDelete={() => {
                      const copy = [...panDoc];
                      copy.splice(i, 1);
                      setPanDoc(copy);
                    }}
                  />
                ))}
              </div>
            )}
          </Form.Item>
        </Col>
        <Col xs={24} md={12} className="pan-right">
          <div className="upload-side">
            <span className="upload-hint">
              Owner/ Partner/ Director’s PAN card
              <br />
              <span className="text"></span>Add for all the directors
            </span>
            <InlineUpload
              fileList={panDoc}
              onChange={(list) => {
                const validList = filterValidFiles(list);
                if (validList.length !== list.length) {
                  message.error(
                    `Only PDF/JPG/PNG up to ${MAX_FILE_MB}MB allowed`
                  );
                }
                setPanDoc(validList);
                form.validateFields(["pan_card"]);
              }}
              buttonText="Upload"
              maxCount={10}
            ></InlineUpload>
          </div>
        </Col>
      </Row>

      {/* Aadhaar row */}
      <Row
        gutter={[16, 12]}
        align="middle"
        className="pan-row"
        style={{ width: "100%" }}
      >
        <Col xs={24} md={12} className="pan-left">
          <Form.Item
            label={
              <>
                Aadhar card no<Text className="text-danger ml-2"> *</Text>
              </>
            }
            name="aadhaar_card"
            rules={[
              { required: true, message: "Enter Aadhaar number" },
              {
                validator: (_, v) =>
                  validateAadhaar(v)
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Enter a valid 12-digit Aadhaar")
                      ),
              },
            ]}
            className="upload-input"
          >
            <Input placeholder="Enter Aadhaar number" className="pan-input" />
          </Form.Item>

          {aadhaarDoc?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {aadhaarDoc.map((f, i) => (
                <FileRow
                  key={f.uid || f.name + i}
                  name={f.name}
                  onDelete={() => {
                    const copy = [...aadhaarDoc];
                    copy.splice(i, 1);
                    setAadhaarDoc(copy);
                  }}
                />
              ))}
            </div>
          )}
        </Col>

        <Col xs={24} md={12} className="pan-right">
          <div className="upload-side">
            <span className="upload-hint">
              Owner/ Partner/ Director’s Aadhaar card
              <br />
              <span className="text"></span>Add for all the directors
            </span>
            <InlineUpload
              fileList={aadhaarDoc}
              onChange={(list) => {
                const validList = filterValidFiles(list);
                if (validList.length !== list.length) {
                  message.error(
                    `Only PDF/JPG/PNG up to ${MAX_FILE_MB}MB allowed`
                  );
                }
                setAadhaarDoc(validList);
                form.validateFields(["aadhaar_card"]);
              }}
              buttonText="Upload"
              maxCount={10}
            ></InlineUpload>
          </div>
        </Col>
      </Row>

      {/* Email + Secondary */}
      <Col xs={24} md={12}>
        <Form.Item
          label={
            <>
              Email id <Text className="text-danger ml-2"> *</Text>
            </>
          }
          name="email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Enter a valid email" },
          ]}
        >
          <Input placeholder="Enter a valid email" className="pan-input" />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          label="Secondary number"
          name="secondary_phone"
          rules={[
            {
              validator: (_, v) =>
                validateSecondaryPhone(v)
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error("Enter a valid 10-digit mobile number")
                    ),
            },
          ]}
        >
          <Input
            maxLength={12}
            placeholder="Enter a valid 10-digit mobile number"
            className="pan-input"
          />
        </Form.Item>
      </Col>

      {/* MSME Certificate */}

      <Row
        gutter={[16, 12]}
        align="middle"
        className="pan-row"
        style={{ width: "100%" }}
      >
        <Col xs={24} md={12} className="pan-left">
          <div className="msme-title">
            <span className="icon-image">
              <img src={docImg} alt="doc" className="doc-icon" />
            </span>
            <Space direction="vertical" size={0}>
              <Text strong>MSME Certificate</Text>
              <Text type="secondary">
                Optional but recommended to speed up approvals
              </Text>
            </Space>
          </div>
        </Col>
        <Col xs={24} md={12} className="pan-right">
          <Upload
            showUploadList={false}
            beforeUpload={() => false}
            maxCount={1}
            onChange={({ fileList }) => {
              const validList = filterValidFiles(fileList);
              if (validList.length !== fileList.length) {
                message.error(
                  `Only PDF/JPG/PNG up to ${MAX_FILE_MB}MB allowed`
                );
              }
              setMsmeDoc(validList);
            }}
          >
            <Button size="large" className="upload-btn">
              Upload
            </Button>
          </Upload>
        </Col>
      </Row>

      {/* Billing address note */}
      <Row
        gutter={[16, 12]}
        align="middle"
        className="pan-row"
        style={{ width: "100%" }}
      >
        <Col span={24} className="billing-address-note">
          <Col flex="auto">
            <Text className="text-primary-font">
              <strong>Billing Address</strong>
            </Text>
            <br />
            <Text type="secondary">
              mobCREDIT tagged billing address needs to be created if it doesn’t
              exist during the time of first order
            </Text>
          </Col>
        </Col>
      </Row>

      {/* Risk assessment */}
      <Row gutter={[16, 12]} align="middle" style={{ width: "100%" }}>
        <Col span={24}>
          <Text className="page-subtitle">Risk assessment</Text>
          <br />
          <Text type="secondary">Please add the filing details</Text>

          <div style={{ marginTop: 8 }}>
            <Table
              pagination={false}
              dataSource={riskRows}
              columns={riskColumns}
              rowKey="key"
              bordered
              size="small"
            />
            <Row justify="end" style={{ marginTop: 8 }}>
              <Text strong>
                Total points:&nbsp;
                {(() => {
                  const vals = form.getFieldsValue();
                  const s1 =
                    riskOptions.find(
                      (o) =>
                        o.value ===
                        (vals.risk_gstr3b ?? initialValues.risk_gstr3b)
                    )?.score ?? 0;
                  const s2 =
                    riskOptions.find(
                      (o) =>
                        o.value ===
                        (vals.risk_gstr1 ?? initialValues.risk_gstr1)
                    )?.score ?? 0;
                  return s1 + s2;
                })()}
              </Text>
            </Row>
          </div>
        </Col>
      </Row>
    </Row>
  );

  /* ----------------------------- STEP 2 (polished) ----------------------------- */
  const step2 = (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Text className="page-subtitle">Trade credit details</Text>
      </Col>

      {/* Status */}
      <Row gutter={[16, 16]} className="billing-address-note">
        <Col span={24}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // background: '#fafafa',
              // borderRadius: 12,
              // padding: '18px 24px',
              // border: '1px solid #e7edf3',
              // marginBottom: 16,
              width: "100%",
            }}
          >
            <span className="icon-image" style={{ marginRight: 16 }}>
              <img src={docImg} alt="doc" className="doc-icon" />
            </span>
            <span
              style={{
                fontWeight: 600,
                color: "#0a243f",
                fontSize: 16,
                flex: 1,
              }}
            >
              Status <span style={{ color: "red" }}>*</span>
            </span>
            <Form.Item
              label=""
              name="status"
              rules={[{ required: true, message: "Please select status" }]}
            >
              <Select
                style={{ width: "300px", fontWeight: 600, marginTop: "12px" }}
                options={[
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                  { value: "blocked", label: "Blocked" },
                  { value: "overdue", label: "Overdue" },

                ]}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>

      {/* Amount sanctioned */}
      <Row className="billing-address-note" gutter={[16, 16]}>
        <Col span={24}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              // background: '#fafafa',
              // borderRadius: 12,
              paddingTop: "12px",
              paddingBottom: "12px",
              // border: '1px solid #e7edf3',
              // marginBottom: 16,
              width: "100%",
            }}
          >
            <span className="icon-image" style={{ marginRight: 16 }}>
              <img src={docImg} alt="doc" className="doc-icon" />
            </span>
            <span
              style={{
                fontWeight: 600,
                color: "#0a243f",
                fontSize: 16,
                flex: 1,
              }}
            >
              Amount sanctioned <span style={{ color: "red" }}>*</span>
            </span>
            <Form.Item
              name="amount_sanctioned"
              rules={[{ required: true, message: "Please enter amount" }]}
              style={{ marginBottom: 0, flex: 1, maxWidth: 300 }}
            >
              <InputNumber
                min={0}
                precision={2}
                style={{ width: "100%", fontWeight: 600 }}
                placeholder="1,000,000.00"
                formatter={(value) => `₹ ${value}`}
                parser={(value) => value.replace(/₹\s?|(,*)/g, "")}
              />
            </Form.Item>
          </div>
        </Col>
      </Row>
      {/* Terms & conditions */}
      <Row gutter={[16, 16]} className="billing-address-note">
        <Col span={24}>
          <Text strong>Terms and conditions</Text>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            label="Line of credit duration"
            name="line_of_credit_duration"
            rules={[{ required: true, message: "Select duration" }]}
          >
            <Select
              options={[7, 14, 21, 30, 45, 60].map((d) => ({
                value: d,
                label: `${d} days`,
              }))}
              onChange={(val) => {
                form.setFieldsValue({
                  line_of_credit_duration: val,
                  terms: getTermsText(val),
                });
              }}
            />
          </Form.Item>
        </Col>

        <Col span={24}>
          <Form.Item
            label="Terms (shown to customer)"
            name="terms"
            rules={[{ required: true, message: "Please enter terms" }]}
          >
            <Input.TextArea rows={4} autoSize={{ minRows: 4, maxRows: 8 }} />
          </Form.Item>
        </Col>
      </Row>
      {/* PDC section */}

      <Col span={24}>
        <Divider orientation="left" orientationMargin={0}>
          <Space>
            <Text className="page-subtitle">Post dated cheque status</Text>
          </Space>
        </Divider>
        <Text type="secondary">
          Cheque amount should match the sanctioned amount and be without date.
        </Text>
      </Col>
      <Row className="billing-address-note" gutter={[16, 16]}>
        <Col span={24}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              background: "#fafafa",

              marginBottom: 16,
            }}
          >
            <span className="icon-image" style={{ marginRight: 16 }}>
              <img src={docImg} alt="doc" className="doc-icon" />
            </span>
            <span
              style={{
                fontWeight: 600,
                color: "#0a243f",
                fontSize: 16,
                flex: 1,
              }}
            >
              Post dated cheque <span style={{ color: "red" }}>*</span>
            </span>
            <div className="d-flex-center" style={{ gap: "16px" }}>
              <span style={{ fontWeight: 500 }}>Received</span>
              <Form.Item
                name="pdc_not_received"
                valuePropName="checked"
                style={{ marginBottom: 0 }}
              >
                <Switch
                  checkedChildren="Received"
                  unCheckedChildren="Not received"
                />
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Upload
                  multiple
                  showUploadList={false}
                  beforeUpload={() => false}
                  accept=".pdf,.jpg,.jpeg,.png"
                  fileList={pdcFile}
                  onChange={({ fileList }) => {
                    // Add default status for new files
                    setPdcFile(
                      fileList.map((f) => ({
                        ...f,
                        status: f.status || "Active",
                      }))
                    );
                  }}
                >
                  <Button className="upload-btn">Upload</Button>
                </Upload>
              </Form.Item>
            </div>
          </div>
          {/* Render uploaded files with status dropdown */}
          {Array.isArray(pdcFile) && pdcFile.length > 0 && (
            <div>
              {pdcFile.map((file, idx) => (
                <div
                  key={file.uid || file.name + idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    background:
                      file.status === "Cancelled" ? "#fff1f0" : "#f6ffed",
                    borderRadius: 8,
                    padding: "16px",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ color: "#1890ff", flex: 1 }}>{file.name}</span>
                  <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      const copy = [...pdcFile];
                      copy.splice(idx, 1);
                      setPdcFile(copy);
                    }}
                  />
                  <Select
                    value={file.status || "Active"}
                    style={{
                      width: 120,
                      marginLeft: 12,
                      background: "transparent",
                      height: "auto",
                    }}
                    onChange={(val) => {
                      const copy = [...pdcFile];
                      copy[idx].status = val;
                      setPdcFile(copy);
                    }}
                    options={[
                      { value: "Active", label: "Active" },
                      { value: "Cancelled", label: "Cancelled" },
                    ]}
                  />
                </div>
              ))}
            </div>
          )}
        </Col>
        <Col span={24}>
        <Form.Item name="continue_without_cheque" valuePropName="checked">
          <Checkbox>Continue without uploading cheque</Checkbox>
        </Form.Item>
      </Col>
      </Row>
      
      {form.getFieldValue("continue_without_cheque") && (
        <Col span={24}>
          <Form.Item
            label="Reason for not uploading cheque"
            name="reason"
            rules={[
              {
                required: true,
                message: "Reason is required when continuing without cheque",
              },
              {
                validator: (_, v) =>
                  (v || "").trim().length >= 10
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error("Reason must be at least 10 characters")
                      ),
              },
            ]}
          >
            <Input.TextArea
              placeholder="Enter the reason (min 10 characters)"
              rows={3}
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
          </Form.Item>
        </Col>
      )}
    </Row>
  );

  /* ----------------------------- STEP 3 (polished) ----------------------------- */
  const step3 = (
    <Row gutter={[16, 16]}>
      {/* Activation toggle */}
      <Col span={24}>
        <Divider orientation="left" orientationMargin={0}>
          <Space>
            {/* <Tag color="purple">Step 3 — Activate</Tag> */}
            <Text className="page-subtitle">
              Activation & customer approval
            </Text>
          </Space>
        </Divider>
      </Col>
      <Col span={24}>
        <Title level={5} className="section-title">
          Send to customer for approval
        </Title>
        <Text type="secondary">Please send this to the customer</Text>

        <Card className="credit-copytext" bodyStyle={{ padding: 16 }} bordered>
          <Space direction="vertical" size="small" style={{ width: "100%" }}>
            <div style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {cleanMessage}
            </div>

            {approvalDetails?.link && (
              <div>
                <Text strong>Approval link: </Text>
                <a href={approvalDetails.link} target="_blank" rel="noreferrer">
                  {approvalDetails.link}
                </a>
              </div>
            )}

            {approvalDetails?.link_expiry && (
              <div>
                <Text type="secondary">
                  Active till: {approvalDetails.link_expiry}
                </Text>
              </div>
            )}

            <Button
              type="default"
              icon={<CopyOutlined />}
              onClick={copyCustomerMsg}
              disabled={!approvalDetails?.message}
            >
              Copy message text
            </Button>
          </Space>
        </Card>
      </Col>

      {/* Link status row */}

      <Row className="billing-address-note">
        <Col span={12}>
          <Space direction="vertical" size={0}>
            <Text strong>Link status *</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Active till: 4 July 2026, 09:30 PM
            </Text>
          </Space>
        </Col>
        <Col span={12} className="text-right">
          <Space>
            <Switch checked disabled /> <Text>Active</Text>
            <Button icon={<ReloadOutlined />}>Regenerate link</Button>
          </Space>
        </Col>
      </Row>

      {/* Cheques dragger (optional) */}
      {/* <Col span={24}>
        <Divider orientation="left" orientationMargin={0}>
          <Space>
            <InboxOutlined /> <Text strong>Cheques (optional)</Text>
          </Space>
        </Divider>
        <Form.Item
          name="cheques"
          valuePropName="fileList"
          getValueFromEvent={norm}
        >
          <Dragger
            multiple
            fileList={cheques}
            beforeUpload={() => false}
            onChange={({ fileList }) => setCheques(fileList)}
            accept=".pdf,.jpg,.jpeg,.png"
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag files to this area to upload
            </p>
            <p className="ant-upload-hint">
              Upload one or more cheque images/PDFs.
            </p>
          </Dragger>
        </Form.Item>
      </Col> */}
    </Row>
  );

  return (
    <div
      className="form-container mobcredit"
      style={{ maxWidth: 1080, margin: "0 auto" }}
    >
      <Card
        className="topbar"
        bordered={false}
        bodyStyle={{ padding: 12 }}
        style={{
          marginBottom: 16,
          boxShadow: "none",
        }}
      >
        <Row align="middle" gutter={8}>
          <Col className="d-flex-center">
            <Button
              className="btn"
              onClick={handleBack}
              icon={<ArrowLeftOutlined />}
            >
              BACK
            </Button>
          </Col>
          <Col flex="auto" align="middle">
            <Steps
              current={current}
              labelPlacement="vertical"
              className="custom-steps"
              items={[
                { title: "Customer details" },
                { title: "Credit amount" },
                { title: "Activation" },
              ]}
            />
          </Col>
        </Row>
      </Card>

      <Card
        bordered={false}
        style={{
          marginBottom: 16,
          boxShadow: "none",
        }}
      >
        <Form
          layout="vertical"
          form={form}
          initialValues={initialValues}
          requiredMark={false}
          colon={false}
          onValuesChange={handleValuesChange}
          validateTrigger="onSubmit"
        >
          {current === 0 ? step1 : current === 1 ? step2 : step3}
        </Form>
      </Card>

      {/* Sticky Footer */}
      <div
        className="sticky-footer"
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: token.colorBgElevated,
          borderTop: `1px solid ${token.colorSplit}`,
          padding: "12px 0",
          marginTop: 16,
          zIndex: 5,
          boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
          borderRadius: 12,
        }}
      >
        <Row justify="center" gutter={16} style={{ width: "100%" }}>
          <Col span={5}></Col>
          <Col>
            <Button className="btn" onClick={handleBack}>
              BACK
            </Button>
          </Col>
          <Col>
            {current < 2 ? (
              <Button
                className="autosave-button"
                onClick={handleContinue}
                loading={saving}
                disabled={saving || canContinue || isCreditBlocked}
                
              >
                AUTOSAVE & CONTINUE
              </Button>
            ) : (
              <Button
                className="autosave-button"
                onClick={onSubmitAll}
                disabled={isCreditBlocked}
              >
                AUTOSAVE & ACTIVATE
              </Button>
            )}
          </Col>
        </Row>
      </div>
    </div>
  );
}

function FilePill({ name, onDelete }) {
  return (
    <div
      className="file-pill"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        background: "#fafafa",
        borderRadius: 999,
        padding: "6px 10px",
        border: "1px solid #f0f0f0",
        marginTop: 6,
      }}
    >
      <PaperClipOutlined />
      <span
        className="file-pill-name"
        style={{
          maxWidth: 260,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <Button
        type="text"
        size="small"
        className="file-pill-delete"
        icon={<DeleteOutlined />}
        onClick={onDelete}
      />
    </div>
  );
}

function FileRow({ name, onDelete }) {
  return (
    <div
      className="file-row"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#fafafa",
        borderRadius: 6,
        padding: "8px 12px",
        border: "1px solid #f0f0f0",
        marginBottom: 8,
      }}
    >
      <span
        className="file-row-name"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {name}
      </span>
      <Button
        type="text"
        className="file-row-delete"
        icon={<DeleteOutlined />}
        onClick={onDelete}
      />
    </div>
  );
}
// eslint-disable-next-line
async function safeError(resp) {
  try {
    const data = await resp.json();
    return data?.message || data?.error || data?.detail || "";
  } catch {
    return "";
  }
}
